import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Button, Checkbox, Col, DatePicker, Form, Input, Row, Select } from 'antd';
import personalDataRepo from '../../services/repository/personalData';
import { FormInstance } from 'antd/lib/form';
import { validateCPF, validateEmail, validateMessages } from '../../services/util/validator';
import { normalizeText } from '../../services/util/normalizeText';
import styled from 'styled-components';
import { CountryBirth } from '../../interfaces/CountryBirth';
import { SkinColor } from '../../interfaces/SkinColor';
import { DegreeEducation } from '../../interfaces/DegreeEducation';
import jsonSex from '../../mocks/sex.json';
import jsonMaritalStatus from '../../mocks/maritalStatus.json';
import RowSection from '../atoms/RowSection';
import { State } from '../../interfaces/State';
import { City } from '../../interfaces/City';
import { PersonalDataRequest } from '../../interfaces/PersonalDataRequest';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import { AdmissionDataRules } from '../../services/util/field-rules-manager/types/fieldRuleTypes';
import AdmissionFormItem from '../molecules/AdmissionFormItem';
import { events } from '../../services/util/field-rules-manager/FieldRuleService';
import BooleanOptionList from '../../constants/BooleanOptionList';
import { isScreenUnderWidth } from '../../services/util/screen';
import DefaultSelectCountry from '../../constants/DefaultSelectCountry';

dayjs.extend(weekday);
dayjs.extend(localeData);

const { Option } = Select;

const ButtonCol = styled(Col)`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
`;

type Props = {
  handleNext: () => void,
  initForm: PersonalDataRequest | undefined,
  admissionDataRules: AdmissionDataRules
}

const PersonalData = React.forwardRef<FormInstance, Props>(({ handleNext, initForm, admissionDataRules }, ref) => {
  const [form] = Form.useForm<PersonalDataRequest | undefined>();
  const formRules = admissionDataRules.personal;

  const [data, setData] = useState<{
    degreeEducation: DegreeEducation[],
    countries: CountryBirth[],
    skinColors: SkinColor[],
    states: State[],
    cities: City[]
    booleanOptionlist: any[],
  }>({ degreeEducation: [], countries: [], skinColors: [], states: [], cities: [], booleanOptionlist: [] });

  // @ts-ignore
  useImperativeHandle(ref, () => ({
    validateFields: () => form.validateFields(),
    submit: () => form.submit(),
    getFieldsValue: form.getFieldsValue,
    setFieldsValue: () => form.setFieldValue,
  }));

  const fetchData = async () => {
    try {
      const [degreeEducation, countriesResponse, skinColorsResponse, stateResponse] = await Promise.all([
        personalDataRepo.findAllDegreeEducation(),
        personalDataRepo.findAllCountries(),
        personalDataRepo.findAllSkinColor(),
        personalDataRepo.findAllStates(),
      ]);

      let cities: City[] = [];

      //trigger city loading if state data is filled
      if (initForm?.birthPlace?.state) {
        cities = await personalDataRepo.findAllCities(initForm?.birthPlace?.state);
      }

      setData({
        degreeEducation: degreeEducation.data,
        countries: countriesResponse.data,
        skinColors: skinColorsResponse.data,
        states: stateResponse.data,
        cities: cities,
        booleanOptionlist: BooleanOptionList,
      });
    } catch (error) {
      console.error('Ocorreu um erro ao buscar os dados:', error);
    }
  };

  const handleChangeCPF = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    const maskedValue = value
      .replace(/\D/g, '') // Remove caracteres não numéricos
      .replace(/(\d{3})(\d)/, '$1.$2') // Insere o primeiro ponto
      .replace(/(\d{3})(\d)/, '$1.$2') // Insere o segundo ponto
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2'); // Insere o hífen

    form.setFieldsValue({
      data: {
        cpf: maskedValue,
      },
    });
  };

  useEffect(() => {
    fetchData().then(r => r);
  }, [initForm]);

  useEffect(() => {
    if (initForm && initForm.data && initForm.data.dateOfBirth != null) {
      initForm.data.dateOfBirth = dayjs(initForm.data.dateOfBirth);
    }
    form.setFieldsValue(initForm);
  }, [initForm]);

  const EXTERIOR_STATE_ID = 'EX';

  const onChangeState = async (value: string) => {
    const cities = await personalDataRepo.findAllCities(value);
    setData({
      ...data, cities: cities,
    });
    
    // Lógica de preenchimento automático do país
    if (value === EXTERIOR_STATE_ID) {
      form.setFieldsValue({
        birthPlace: {
          country: undefined,
        },
      });
    } else if (value) {
      form.setFieldsValue({
        birthPlace: {
          country: DefaultSelectCountry.id, // Brasil (105)
        },
      });
    }
    
    events.triggerFormRuleUpdateEvent();
  };

  const handleChangePhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue: string = e.target.value;
    // Remove todos os caracteres não numéricos
    const cleanedValue: string = rawValue.replace(/\D/g, '');
    // Formata o telefone
    let formattedValue: string = cleanedValue;
    if (cleanedValue.length >= 6) {
      formattedValue = cleanedValue.replace(/^(\d{5})(\d{0,4})?/, '$1-$2');
    }

    form.setFieldsValue({
      contact: {
        cellphone: formattedValue,
      },
    });
  };

  const handleChangeLandLine = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue: string = e.target.value;
    // Remove todos os caracteres não numéricos
    const cleanedValue: string = rawValue.replace(/\D/g, '');
    // Formata o telefone
    let formattedValue: string = cleanedValue;
    if (cleanedValue.length >= 5) {
      formattedValue = cleanedValue.replace(/^(\d{4})(\d{0,4})?/, '$1-$2');
    }

    form.setFieldsValue({
      contact: {
        landline: formattedValue,
      },
    });
  };

  const removeNumericCaracter = (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue: string = e.target.value;
    // Remove todos os caracteres não numéricos
    const cleanedValue: string = rawValue.replace(/\D/g, '');

    form.setFieldsValue({
      contact: {
        [field]: cleanedValue,
      },
    });


    return cleanedValue;
  };

  const onValuesChangeHandler = (changedValues: any, values: PersonalDataRequest | undefined) => {
    if(changedValues.birthPlace){
      events.triggerFormRuleUpdateEvent();
    }
  };

  const isUnder = isScreenUnderWidth(576);

  return (
    <>
      <Form
        onValuesChange={onValuesChangeHandler}
        form={form}
        requiredMark={false}
        layout='vertical'
        validateMessages={validateMessages}
        style={{ maxWidth: 600 }}
      >
        <RowSection>Dados Pessoais</RowSection>

        <Row>
          <Col span={24}>
            <AdmissionFormItem fieldRule={formRules?.nameAffiliation?.name}>
              <Input />
            </AdmissionFormItem>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <AdmissionFormItem fieldRule={formRules?.nameAffiliation?.socialName}>
              <Input />
            </AdmissionFormItem>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules?.data?.cpf}
              formItemProps={{ validateTrigger: 'onBlur',  rules: [{ validator: validateCPF }] }}>
              <Input onChange={handleChangeCPF} maxLength={14} />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules?.data?.dateOfBirth}>
              <DatePicker
                format={'DD/MM/YYYY'}
                placeholder='DD/MM/YYYY'
                style={{ width: '100%' }}
                popupClassName={isUnder ? 'small-date-picker' : ''}
              />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules?.data?.gender}>
              <Select
                placeholder='Selecione uma opção'
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={false}
              >
                {jsonSex.map((s) => (
                  <Option
                    key={`gender-${s.id}`}
                    value={s.id}
                  >
                    {s.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules?.information?.maritalStatus}>
              <Select
                placeholder='Selecione uma opção'
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={false}
              >
                {jsonMaritalStatus.map((marital) => (
                  <Option
                    key={`marital-status-${marital.id}`}
                    value={marital.id}
                  >
                    {marital.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules?.information?.skinColor}>
              <Select
                placeholder='Selecione uma opção'
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={false}
              >
                {data.skinColors.map((skinColor) => (
                  <Option
                    key={`skin-color-${skinColor.id}`}
                    value={skinColor.id}
                  >
                    {skinColor.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

        </Row>

        <RowSection>Filiação</RowSection>

        <Row>
          <Col span={24}>
            <AdmissionFormItem fieldRule={formRules?.nameAffiliation?.father}>
              <Input placeholder='Digite aqui' />
            </AdmissionFormItem>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <AdmissionFormItem fieldRule={formRules?.nameAffiliation?.mother}>
              <Input placeholder='Digite aqui' />
            </AdmissionFormItem>
          </Col>
        </Row>

        <RowSection>Local de Nascimento</RowSection>

        <Row gutter={24}>
          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules?.birthPlace?.country}>
              <Select
                placeholder='Selecione uma opção'
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={isUnder}
                placement='bottomLeft'
              >
                {data.countries.map((country) => (
                  <Option
                    key={`country-${country.id}`}
                    value={country.id}
                  >
                    {country.country}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules?.birthPlace?.state}>
              <Select
                placeholder='Selecione uma opção'
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  normalizeText(option?.props.children ?? '').indexOf(normalizeText(input)) >= 0
                )}
                onChange={(value) => onChangeState(value)}
                popupMatchSelectWidth={isUnder}
              >
                {data.states.map((state) => (
                  <Option
                    key={`state-${state.id}`}
                    value={state.id}
                  >
                    {state.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules?.birthPlace?.city}>
              <Select
                placeholder='Selecione uma opção'
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  normalizeText(option?.props.children ?? '').indexOf(normalizeText(input)) >= 0
                )}
                popupMatchSelectWidth={isUnder}
              >
                {data.cities.map((city) => (
                  <Option
                    key={`city-${city.id}`}
                    value={city.id}
                  >
                    {city.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>
        </Row>

        <RowSection>Contato</RowSection>

        <Row gutter={24}>
          <Col xs={8} sm={8} md={4} lg={4}>
            <AdmissionFormItem fieldRule={formRules?.contact?.dddLandLine}>
              <Input type='text' placeholder='(000)'
                onChange={(e) => removeNumericCaracter('dddLandLine', e)} />
            </AdmissionFormItem>
          </Col>

          <Col xs={16} sm={16} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules?.contact?.landline}>
              <Input type={'text'} placeholder='Digite aqui'
                maxLength={9}
                value={form.getFieldValue('landLine')}
                onChange={(e) => handleChangeLandLine(e)} />
            </AdmissionFormItem>
          </Col>

          <Col xs={8} sm={8} md={4} lg={4}>
            <AdmissionFormItem fieldRule={formRules?.contact?.dddCellPhone}>
              <Input type='text' placeholder='(000)'
                onChange={(e) => removeNumericCaracter('dddCellPhone', e)} />
            </AdmissionFormItem>
          </Col>

          <Col xs={16} sm={16} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules?.contact?.cellphone}>
              <Input placeholder='Digite aqui' maxLength={10} onChange={(e) => handleChangePhone(e)} />
            </AdmissionFormItem>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <AdmissionFormItem fieldRule={formRules?.information?.email} formItemProps={{ rules: [{ validator: validateEmail, max: 250 }]}}>
              <Input type='email' placeholder='Digite aqui' />
            </AdmissionFormItem>
          </Col>
        </Row>


        <RowSection>Dados Profissionais e Escolaridade</RowSection>

        <Row gutter={24}>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules?.information?.educationLevel}>
              <Select
                placeholder='Selecione uma opção'
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={isUnder}
              >
                {data.degreeEducation.map((education) => (
                  <Option
                    key={`degree-education-${education.id}`}
                    value={education.id}
                  >
                    {education.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules?.information?.socialSecurityBenefit}>
              <Select
                showSearch
                optionFilterProp='children'
                allowClear
                popupMatchSelectWidth={false}
              >
                {data.booleanOptionlist.map(option => (
                  <Option
                    key={option.description}
                    value={option.value}
                  >
                    {option.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules?.information?.firstJob}>
              <Select
                showSearch
                optionFilterProp='children'
                allowClear
                popupMatchSelectWidth={false}
              >
                {data.booleanOptionlist.map(option => (
                  <Option
                    key={option.description}
                    value={option.value}
                  >
                    {option.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>
        </Row>
      </Form>

      <Row>
        <ButtonCol span={24}>
          <Button type={'primary'} onClick={() => handleNext()}>Prosseguir</Button>
        </ButtonCol>
      </Row>
    </>
  );
});

PersonalData.displayName = 'PersonalData';

export default PersonalData;
